import { warehouseClient, extractStatusCode, extractResponseMessage } from "@/infrastructure/http/warehouseClient";
import { PlanLimitError } from "@/infrastructure/errors/planLimitError";
import type {
  ProductListResponse,
  CreateProductInput,
  UpdateProductInput,
  UpdateProductStatusInput,
  BulkProductItem,
  BulkProductResult,
  ProductErrorCode,
  ApiEnvelope,
} from "@/domain/types";

export function createProductError(code: ProductErrorCode): Error {
  const err = new Error(code);
  err.name = "ProductError";
  return err;
}

export function isProductError(err: unknown): err is Error & { message: ProductErrorCode } {
  return err instanceof Error && err.name === "ProductError";
}

function mapError(statusCode: number): ProductErrorCode {
  if (statusCode === 409) return "DUPLICATE_CODE";
  if (statusCode === 400) return "VALIDATION";
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listProducts(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  categoryId?: string;
}): Promise<ProductListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);
  if (params.categoryId) query.set("categoryId", params.categoryId);

  const response = await warehouseClient.get<ApiEnvelope<ProductListResponse>>(
    `/product?${query.toString()}`
  );
  return response.data.data;
}

export async function createProduct(input: CreateProductInput): Promise<void> {
  try {
    await warehouseClient.post("/product", input);
  } catch (err) {
    const status = extractStatusCode(err);
    if (status === 403) {
      const msg = extractResponseMessage(err);
      if (msg) throw new PlanLimitError(msg);
    }
    throw createProductError(mapError(status));
  }
}

export async function updateProduct(input: UpdateProductInput): Promise<void> {
  try {
    await warehouseClient.patch("/product", input);
  } catch (err) {
    throw createProductError(mapError(extractStatusCode(err)));
  }
}

export async function updateProductStatus(input: UpdateProductStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/product/status", input);
  } catch (err) {
    throw createProductError(mapError(extractStatusCode(err)));
  }
}

export async function bulkImportProducts(products: BulkProductItem[]): Promise<BulkProductResult> {
  const response = await warehouseClient.post<ApiEnvelope<BulkProductResult>>(
    "/product/bulk",
    { products }
  );
  return response.data.data;
}

export async function importProductsCsv(
  file: File,
  typeUnitId: string,
  mapping: Record<string, string>
): Promise<{ jobId: string; status: string; totalRows: number }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("typeUnitId", typeUnitId);
  formData.append("mapping", JSON.stringify(mapping));

  const response = await warehouseClient.post<ApiEnvelope<{ jobId: string; status: string; totalRows: number }>>(
    "/product/import",
    formData,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 }
  );
  return response.data.data;
}

export async function getImportJobStatus(jobId: string): Promise<ImportJobStatus> {
  const response = await warehouseClient.get<ApiEnvelope<ImportJobStatus>>(`/product/import/${jobId}`);
  return response.data.data;
}

export async function listImportJobs(): Promise<ImportJobStatus[]> {
  const response = await warehouseClient.get<ApiEnvelope<ImportJobStatus[]>>("/product/import");
  return response.data.data ?? [];
}
